import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  OpenInNew as DetailsIcon,
  Search as SearchIcon,
  Explore as ExploreIcon,
  CheckCircleOutline as UsingIcon,
  HighlightOff as NotUsingIcon,
  Login as LoginIcon,
  Payments as FeeIcon,
  FactCheck as AttendanceIcon,
  Badge as StaffAttIcon,
  MenuBook as HomeworkIcon,
  AccountBalanceWallet as ExpenseIcon,
  Campaign as NoticeIcon,
  CheckCircle as TickIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

//Created By Cursor
const PERIODS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_10_days", label: "Last 10 Days" },
  { value: "last_15_days", label: "Last 15 Days" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "last_30_days", label: "Last 30 Days" },
];

//Created By Cursor
const ACTIVITIES = [
  {
    value: "login",
    label: "Staff login",
    short: "Logins",
    color: crmColors.info.main,
    icon: <LoginIcon />,
  },
  {
    value: "fee",
    label: "Fee collection",
    short: "Fees",
    color: crmColors.energy.main,
    icon: <FeeIcon />,
  },
  {
    value: "attendance",
    label: "Student attendance",
    short: "Stu att.",
    color: crmColors.warning.main,
    icon: <AttendanceIcon />,
  },
  {
    value: "staff_attendance",
    label: "Staff attendance",
    short: "Staff att.",
    color: crmColors.secondary.main,
    icon: <StaffAttIcon />,
  },
  {
    value: "homework",
    label: "Homework",
    short: "Homework",
    color: crmColors.primary.dark || crmColors.primary.main,
    icon: <HomeworkIcon />,
  },
  {
    value: "expense",
    label: "Expense",
    short: "Expense",
    color: crmColors.error.main,
    icon: <ExpenseIcon />,
  },
  {
    value: "notice",
    label: "Notice",
    short: "Notice",
    color: crmColors.primary.main,
    icon: <NoticeIcon />,
  },
];

const PAGE_SIZES = [50, 100, 200, 500, 1000];

//Created By Cursor
const SORT_OPTIONS = [
  { value: "records_desc", label: "Records ↓" },
  { value: "records_asc", label: "Records ↑" },
  { value: "name_asc", label: "Name A–Z" },
  { value: "name_desc", label: "Name Z–A" },
  { value: "code_asc", label: "Code A–Z" },
  { value: "code_desc", label: "Code Z–A" },
];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

//Created By Cursor
const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const SELECTED_GOLD = crmColors.logo.brand; // #F9A602
const SELECTED_GREEN = crmColors.success.main; // #0C7300

//Created By Cursor
const StatCard = ({ label, value, color, icon, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      position: "relative",
      flex: "1 1 108px",
      minWidth: 0,
      p: 1.25,
      pr: active ? 2.25 : 1.25,
      borderRadius: "14px",
      cursor: onClick ? "pointer" : "default",
      border: `1.5px solid ${
        active ? SELECTED_GOLD : alpha(color, 0.18)
      }`,
      background: active
        ? `linear-gradient(135deg, ${alpha(SELECTED_GOLD, 0.55)} 0%, ${alpha(
            "#C8E6A0",
            0.85
          )} 48%, ${alpha(SELECTED_GREEN, 0.18)} 100%)`
        : `linear-gradient(145deg, ${alpha(color, 0.07)} 0%, #fff 75%)`,
      boxShadow: active
        ? `0 0 0 1px ${alpha(SELECTED_GOLD, 0.45)}, 0 8px 18px ${alpha(
            SELECTED_GOLD,
            0.28
          )}`
        : "none",
      display: "flex",
      alignItems: "center",
      gap: 1.15,
      transition: "transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease",
      "&:hover": onClick
        ? {
            transform: "translateY(-1px)",
            borderColor: active ? SELECTED_GOLD : alpha(color, 0.42),
            boxShadow: active
              ? `0 0 0 1px ${alpha(SELECTED_GOLD, 0.4)}, 0 10px 20px ${alpha(
                  SELECTED_GOLD,
                  0.26
                )}`
              : `0 8px 18px ${alpha(color, 0.12)}`,
          }
        : undefined,
    }}
  >
    {active ? (
      <TickIcon
        sx={{
          position: "absolute",
          top: 6,
          right: 6,
          fontSize: 18,
          color: SELECTED_GREEN,
          filter: `drop-shadow(0 0 1px ${alpha(SELECTED_GOLD, 0.55)})`,
        }}
      />
    ) : null}
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: "10px",
        display: "grid",
        placeItems: "center",
        bgcolor: active ? alpha(SELECTED_GOLD, 0.22) : alpha(color, 0.14),
        color: active ? SELECTED_GREEN : color,
        flexShrink: 0,
        "& .MuiSvgIcon-root": { fontSize: 18 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: active ? SELECTED_GREEN : crmColors.text.secondary,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: 20,
          color: active ? crmColors.logo.dark : crmColors.text.primary,
          lineHeight: 1.15,
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

//Created By Cursor
const ExploreActivity = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activity, setActivity] = useState(searchParams.get("activity") || "login");
  const [range, setRange] = useState(searchParams.get("range") || "today");
  const [tab, setTab] = useState(
    searchParams.get("tab") === "not_using" ? "not_using" : "using"
  );
  const [sort, setSort] = useState(
    SORT_OPTIONS.some((o) => o.value === searchParams.get("sort"))
      ? searchParams.get("sort")
      : "records_desc"
  );
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [kpi, setKpi] = useState({});
  const [summary, setSummary] = useState(null);
  const [rangeLabel, setRangeLabel] = useState("Today");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const activityMeta = useMemo(
    () => ACTIVITIES.find((a) => a.value === activity) || ACTIVITIES[0],
    [activity]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getSopExploreActivity({
        q: query,
        range,
        activity,
        tab,
        sort,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      if (res.success) {
        setRows(Array.isArray(res.data) ? res.data : []);
        setTotal(res.total || 0);
        setKpi(res.kpi || {});
        setSummary(res.summary || null);
        setRangeLabel(res.range_label || range);
        setDateFrom(res.date_from || "");
        setDateTo(res.date_to || "");
        const maxPage = Math.max(1, Math.ceil((res.total || 0) / pageSize));
        if (page > maxPage) setPage(maxPage);
      } else {
        toast.error(res.error || res.message || "Failed to load explore activity");
        setRows([]);
        setTotal(0);
      }
    } catch (e) {
      toast.error(e?.message || "Failed to load explore activity");
    } finally {
      setLoading(false);
    }
  }, [query, range, activity, tab, sort, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setQuery(search.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const next = new URLSearchParams();
    next.set("activity", activity);
    next.set("range", range);
    next.set("tab", tab);
    next.set("sort", sort);
    setSearchParams(next, { replace: true });
  }, [activity, range, tab, sort, setSearchParams]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const usingCount = summary?.using_count ?? 0;
  const notUsingCount = summary?.not_using_count ?? 0;
  const totalActive = summary?.total_schools_active ?? 0;
  const accent = activityMeta.color;
  const dateLine =
    dateFrom && dateTo
      ? dateFrom === dateTo
        ? formatDate(dateFrom)
        : `${formatDate(dateFrom)} – ${formatDate(dateTo)}`
      : "";

  const selectActivity = (value) => {
    setPage(1);
    setActivity(value);
  };

  const selectRange = (value) => {
    if (!value) return;
    setPage(1);
    setRange(value);
  };

  return (
    <PageContentLayout
      title="Explore Activity"
      subtitle={`${activityMeta.label} · ${rangeLabel}${dateLine ? ` · ${dateLine}` : ""}`}
      barSubtitle={`${totalActive} active schools · ${usingCount} working · ${notUsingCount} not working`}
      breadcrumbs={[
        {
          label: "Explore Activity",
          icon: <ExploreIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
        },
      ]}
      backTo="/sop"
      darkBar
      actions={
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate("/sop")}
          sx={{
            textTransform: "none",
            fontWeight: 800,
            borderRadius: "10px",
            bgcolor: crmColors.energy.main,
            "&:hover": { bgcolor: crmColors.energy.dark },
          }}
        >
          Back to SOP
        </Button>
      }
    >
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} useFlexGap flexWrap="wrap">
          {ACTIVITIES.map((a) => (
            <StatCard
              key={a.value}
              label={a.short}
              value={kpi?.[a.value] ?? 0}
              color={a.color}
              icon={a.icon}
              active={activity === a.value}
              onClick={() => selectActivity(a.value)}
            />
          ))}
        </Stack>

        <Box
          sx={{
            p: { xs: 1.35, md: 1.6 },
            borderRadius: "16px",
            border: `1px solid ${crmColors.border.default}`,
            background: `linear-gradient(165deg, ${alpha(accent, 0.07)} 0%, ${
              crmColors.background.paper
            } 55%)`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            alignItems={{ md: "center" }}
            justifyContent="space-between"
            sx={{ mb: 1.35 }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: crmColors.text.secondary,
                }}
              >
                Period
              </Typography>
              <Typography sx={{ mt: 0.2, fontWeight: 800, fontSize: 15, color: accent }}>
                {rangeLabel}
                {dateLine ? (
                  <Typography
                    component="span"
                    sx={{
                      ml: 0.75,
                      fontWeight: 600,
                      fontSize: 12.5,
                      color: crmColors.text.secondary,
                    }}
                  >
                    {dateLine}
                  </Typography>
                ) : null}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={activityMeta.label}
              sx={{
                height: 28,
                fontWeight: 800,
                bgcolor: alpha(accent, 0.12),
                color: accent,
                border: `1px solid ${alpha(accent, 0.22)}`,
              }}
            />
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(5, minmax(0, 1fr))",
                md: "repeat(5, minmax(0, 1fr))",
              },
              gap: 0.85,
            }}
          >
            {PERIODS.map((p) => {
              const selected = range === p.value;
              return (
                <Box
                  key={p.value}
                  onClick={() => selectRange(p.value)}
                  sx={{
                    px: 0.65,
                    py: 1,
                    minWidth: 0,
                    borderRadius: "10px",
                    cursor: "pointer",
                    textAlign: "center",
                    fontWeight: 800,
                    fontSize: { xs: 11.5, md: 12 },
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    border: `1px solid ${
                      selected ? alpha(accent, 0.45) : alpha(crmColors.border.default, 0.95)
                    }`,
                    bgcolor: selected ? alpha(accent, 0.14) : alpha("#fff", 0.88),
                    color: selected ? accent : crmColors.text.primary,
                    boxShadow: selected ? `0 4px 12px ${alpha(accent, 0.14)}` : "none",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: alpha(accent, 0.4),
                      bgcolor: alpha(accent, selected ? 0.16 : 0.06),
                    },
                  }}
                >
                  {p.label}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box
          sx={{
            p: 1.5,
            borderRadius: "16px",
            border: `1px solid ${crmColors.border.default}`,
            bgcolor: crmColors.background.paper,
            boxShadow: `0 6px 18px ${alpha(crmColors.primary.main, 0.04)}`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            alignItems={{ md: "center" }}
          >
            <TextField
              select
              size="small"
              label="Activity"
              value={activity}
              onChange={(e) => selectActivity(e.target.value)}
              sx={{ ...fieldSx, minWidth: 190 }}
            >
              {ACTIVITIES.map((a) => (
                <MenuItem key={a.value} value={a.value}>
                  {a.label}
                  {typeof kpi?.[a.value] === "number" ? ` (${kpi[a.value]})` : ""}
                </MenuItem>
              ))}
            </TextField>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={tab}
              onChange={(_, v) => {
                if (!v) return;
                setPage(1);
                setTab(v);
              }}
              sx={{
                bgcolor: alpha(crmColors.primary.main, 0.03),
                borderRadius: "10px",
                p: 0.35,
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: 12,
                  px: 1.5,
                  border: "none",
                  borderRadius: "8px !important",
                  color: crmColors.text.secondary,
                  "&.Mui-selected": {
                    bgcolor: tab === "using" ? crmColors.success.main : crmColors.error.main,
                    color: "#fff",
                    "&:hover": {
                      bgcolor:
                        tab === "using" ? crmColors.success.dark : crmColors.error.dark,
                    },
                  },
                },
              }}
            >
              <ToggleButton value="using">Working ({usingCount})</ToggleButton>
              <ToggleButton value="not_using">Not working ({notUsingCount})</ToggleButton>
            </ToggleButtonGroup>

            <TextField
              size="small"
              placeholder="Search school, code, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...fieldSx, minWidth: { md: 240 }, flex: 1 }}
            />

            <TextField
              select
              size="small"
              label="Sort"
              value={sort}
              onChange={(e) => {
                setPage(1);
                setSort(e.target.value);
              }}
              sx={{ ...fieldSx, minWidth: 140 }}
            >
              {SORT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Show"
              value={pageSize}
              onChange={(e) => {
                setPage(1);
                setPageSize(Number(e.target.value));
              }}
              sx={{ ...fieldSx, minWidth: 120 }}
            >
              {PAGE_SIZES.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </TextField>

            <Chip
              size="small"
              label={`${total} in list`}
              sx={{
                height: 32,
                fontWeight: 800,
                bgcolor: alpha(accent, 0.12),
                color: accent,
              }}
            />
          </Stack>
        </Box>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
              borderRadius: "16px",
              border: `1px dashed ${crmColors.border.default}`,
              bgcolor: alpha(crmColors.primary.main, 0.02),
            }}
          >
            <CircularProgress size={28} sx={{ color: accent }} />
          </Box>
        ) : rows.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 7,
              px: 2,
              borderRadius: "16px",
              border: `1px dashed ${crmColors.border.default}`,
              bgcolor: alpha(accent, 0.03),
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                mx: "auto",
                mb: 1.25,
                borderRadius: "14px",
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(accent, 0.12),
                color: accent,
              }}
            >
              <ExploreIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography sx={{ fontWeight: 800, color: crmColors.text.primary }}>
              No schools in this list
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              Try another activity, period, or search
            </Typography>
          </Box>
        ) : (
          <>
            <Stack spacing={1}>
              {rows.map((row, index) => {
                const serial = (page - 1) * pageSize + index + 1;
                const statusColor =
                  tab === "using" ? crmColors.success.main : crmColors.error.main;
                return (
                  <Box
                    key={row.school_code}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      borderRadius: "14px",
                      overflow: "hidden",
                      border: `1px solid ${crmColors.border.default}`,
                      background: `linear-gradient(120deg, ${crmColors.background.paper} 0%, ${alpha(
                        statusColor,
                        0.04
                      )} 100%)`,
                      transition: "border-color 0.16s ease, box-shadow 0.16s ease",
                      "&:hover": {
                        borderColor: alpha(accent, 0.28),
                        boxShadow: `0 8px 20px ${alpha(accent, 0.08)}`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 4,
                        alignSelf: "stretch",
                        bgcolor: statusColor,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        width: 34,
                        flexShrink: 0,
                        fontWeight: 800,
                        fontSize: 13,
                        color: crmColors.text.secondary,
                        textAlign: "center",
                        py: 1.35,
                      }}
                    >
                      {serial}
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0, py: 1.25, pr: 0.5 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: 14.5 }}>
                        {row.school_name || "—"}
                      </Typography>
                      <Typography
                        component="div"
                        sx={{
                          fontSize: 12,
                          color: crmColors.text.secondary,
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 0.5,
                          mt: 0.15,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: "inline-block",
                            px: 0.75,
                            py: 0.1,
                            borderRadius: "6px",
                            fontWeight: 800,
                            fontSize: 11.5,
                            letterSpacing: "0.02em",
                            bgcolor: alpha(crmColors.logo.brand, 0.18),
                            color: crmColors.logo.dark,
                            border: `1px solid ${alpha(crmColors.logo.brand, 0.35)}`,
                          }}
                        >
                          {row.school_code}
                        </Box>
                        {row.admin_phone ? (
                          <Box component="span">· {row.admin_phone}</Box>
                        ) : null}
                        {row.admin_name ? (
                          <Box component="span">· {row.admin_name}</Box>
                        ) : null}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      icon={
                        tab === "using" ? (
                          <UsingIcon sx={{ fontSize: "14px !important" }} />
                        ) : (
                          <NotUsingIcon sx={{ fontSize: "14px !important" }} />
                        )
                      }
                      label={tab === "using" ? "Working" : "Not working"}
                      sx={{
                        height: 26,
                        fontWeight: 800,
                        bgcolor: alpha(statusColor, 0.12),
                        color:
                          tab === "using"
                            ? crmColors.success.dark || statusColor
                            : statusColor,
                        "& .MuiChip-icon": { color: "inherit" },
                      }}
                    />
                    <Chip
                      size="small"
                      label={`${Number(row.record_count || 0).toLocaleString()} ${
                        activityMeta.short || "records"
                      }`}
                      sx={{
                        height: 26,
                        minWidth: 72,
                        fontWeight: 800,
                        fontSize: 12,
                        mr: 0.5,
                        bgcolor: alpha(accent, 0.12),
                        color: accent,
                        border: `1px solid ${alpha(accent, 0.22)}`,
                      }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      disabled={!row.school_code}
                      startIcon={<DetailsIcon sx={{ fontSize: 14 }} />}
                      onClick={() =>
                        navigate(
                          `/sop/school/${encodeURIComponent(row.school_code)}`
                        )
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 800,
                        fontSize: 11.5,
                        height: 30,
                        minWidth: 88,
                        mr: 1.5,
                        px: 1.25,
                        borderRadius: "8px",
                        bgcolor: crmColors.primary.main,
                        boxShadow: "none",
                        "&:hover": {
                          bgcolor: crmColors.primary.dark,
                          boxShadow: "none",
                        },
                      }}
                    >
                      Explore
                    </Button>
                  </Box>
                );
              })}
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              alignItems="center"
              justifyContent="space-between"
              sx={{
                px: 1.25,
                py: 1,
                borderRadius: "12px",
                border: `1px solid ${crmColors.border.default}`,
                bgcolor: alpha(crmColors.primary.main, 0.02),
              }}
            >
              <Typography
                sx={{
                  fontSize: 12.5,
                  color: crmColors.text.secondary,
                  fontWeight: 700,
                }}
              >
                Showing {from}–{to} of {total}
              </Typography>
              <Pagination
                color="primary"
                shape="rounded"
                size="small"
                count={pageCount}
                page={page}
                onChange={(_, p) => setPage(p)}
                siblingCount={1}
                boundaryCount={1}
                disabled={loading || pageCount <= 1}
                sx={{ "& .MuiPaginationItem-root": { fontWeight: 700 } }}
              />
            </Stack>
          </>
        )}
      </Stack>
    </PageContentLayout>
  );
};

export default ExploreActivity;
